import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator'
import {
    ShipmentRulesModalBasicDataState
} from '@/modules/configurationModule/detail/shipments/shipment-rules/components/shipment-rules-modal-basic-form/shipment-rules-modal-basic-form.model'
import { CommonLaboratoryResponse, EnumResponse } from '@/api/services/common.dto'
import { shipmentRulesAuxiliaryService } from '@/modules/configurationModule/detail/shipments/shipment-rules/services/shipment-rules-auxiliary/shipment-rules-auxiliary.service'
import {
    ShipmentRuleEntityTypeEnum
} from '@/modules/configurationModule/detail/shipments/shipment-rules/models/shipment-rules.model'

import { Nullable } from '@/shared/types/app.types'
import { commonSampleClassificationService } from '@/api/resources/common/sample-classification/sample-classification.service'
import { cloneDeep } from 'lodash'
import { commonLaboratoryService } from '@/api/resources/common/laboratory/common-laboratory.service'

@Component
export default class ShipmentRulesModalBasicForm extends Vue {
    @Prop(String) readonly actionTitle!: string
    @Prop() basicData!: ShipmentRulesModalBasicDataState
    @Prop() readonly isManual!: boolean

    @Emit()
    create(): ShipmentRulesModalBasicDataState {
        return this.state
    }

    @Emit()
    update(): ShipmentRulesModalBasicDataState {
        return this.state
    }

    @Emit('caseOwnerLaboratoryChanged')
    onCaseOwnerLaboratoryChanged(): Nullable<CommonLaboratoryResponse> {
        return this.state.caseOwnerLaboratory
    }

    @Emit('entityTypeChanged')
    onEntityTypeChanged(): Nullable<EnumResponse> {
        return this.state.entityType
    }

    @Watch('state.entityType')
    onEntityTypeChange() {
        this.onEntityTypeChanged()
    }

    async onCaseOwnerLaboratoryChange(): Promise<void> {
        if (this.state.caseOwnerLaboratory) {
            await this.loadSampleTypesTopographicCodesAndAssays(this.state.caseOwnerLaboratory.labId)
        }
        this.onCaseOwnerLaboratoryChanged()
    }

    @Watch('basicData')
    async onBasicDataChange(): Promise<void> {
        this.state = cloneDeep(this.basicData)
        await this.$validator.reset()
    }

    state: ShipmentRulesModalBasicDataState

    get destinationLaboratoryOrEntityTypeChanged(): string {
        return `${this.state.destinationLaboratory?.labId}/${this.state.entityType?.code}`
    }

    get sampleTypeTopographicCodeAssayComboDisabled(): boolean {
        return !this.state.caseOwnerLaboratory
    }

    get originStateComboDisabled(): boolean {
        return (!this.state.entityType || !this.state.originLaboratory)
    }

    get destinationStateComboDisabled(): boolean {
        return (!this.state.entityType || !this.state.destinationLaboratory)
    }

    get modelValidated(): boolean {
        const hasErrors: boolean = this.$validator.errors.any()
        const isDirty: boolean = this.$_someFieldIsDirty(this.fields)
        return !hasErrors && isDirty
    }

    async loadSampleTypesTopographicCodesAndAssays(laboratoryId: number): Promise<void> {
        const sampleTypesResponse = await this.$_toCallUnique(commonSampleClassificationService.getCommonSampleTypeSampleClassificationsByLaboratory({ laboratoryId }))
        if (sampleTypesResponse) {
            this.state.comboOptions.sampleTypes = sampleTypesResponse
        }
        const topographicCodesResponse = await this.$_toCallUnique(commonSampleClassificationService.getCommonTopographicCodesSampleClassificationsByLaboratory({ laboratoryId }))
        if (topographicCodesResponse) {
            this.state.comboOptions.topographicCodes = topographicCodesResponse
        }
    }

    async getStates(laboratoryId: number): Promise<EnumResponse[]> {
        let states: EnumResponse[] = []
        switch (this.state.entityType?.code) {
        case ShipmentRuleEntityTypeEnum.SAMPLE:
            states = await this.getAllSampleInternalStatesByLaboratory(laboratoryId)
            break
        case ShipmentRuleEntityTypeEnum.PORTION:
            states = await this.getAllPortionInternalStatesByLaboratory(laboratoryId)
            break
        case ShipmentRuleEntityTypeEnum.SUBPORTION:
            states = await this.getAllSubportionInternalStatesByLaboratory(laboratoryId)
            break
        }
        return states
    }

    async getAllSampleInternalStatesByLaboratory(laboratoryId: Nullable<number>): Promise<EnumResponse[]> {
        const response = await this.$_toCallUnique(shipmentRulesAuxiliaryService.getAllSamplesInternalStatesByLaboratory(laboratoryId))
        if (response) {
            return response
        }
        return []
    }
    async getAllPortionInternalStatesByLaboratory(laboratoryId: Nullable<number>): Promise<EnumResponse[]> {
        const response = await this.$_toCallUnique(shipmentRulesAuxiliaryService.getAllPortionsInternalStatesByLaboratory(laboratoryId))
        if (response) {
            return response
        }
        return []
    }

    async getAllSubportionInternalStatesByLaboratory(laboratoryId: Nullable<number>): Promise<EnumResponse[]> {
        const response = await this.$_toCallUnique(shipmentRulesAuxiliaryService.getAllSubportionsInternalStatesByLaboratory(laboratoryId))
        if (response) {
            return response
        }
        return []
    }

    get allShipmentRulesElementsTypes(): EnumResponse[] {
        return [
            { code: ShipmentRuleEntityTypeEnum.SAMPLE, translatedName: this.$t('entities.coreEntities.sample').toString() },
            { code: ShipmentRuleEntityTypeEnum.PORTION, translatedName: this.$t('entities.coreEntities.portion').toString() },
            { code: ShipmentRuleEntityTypeEnum.SUBPORTION, translatedName: this.$t('entities.coreEntities.subportion').toString() }
        ]
    }

    constructor() {
        super()
        this.state = cloneDeep(this.basicData)
    }

    async loadOriginStates(): Promise<void> {
        if (this.state.originLaboratory) {
            this.state.comboOptions.originStates = await this.getStates(this.state.originLaboratory.labId)
        }
    }
    async loadDestinationStates(): Promise<void> {
        if (this.state.destinationLaboratory) {
            this.state.comboOptions.destinationStates = await this.getStates(this.state.destinationLaboratory.labId)
        }
    }
    async loadStates(): Promise<void> {
        const promises = [
            this.loadOriginStates(),
            this.loadDestinationStates()
        ]
        if (this.state.caseOwnerLaboratory) {
            promises.push(this.loadSampleTypesTopographicCodesAndAssays(this.state.caseOwnerLaboratory!.labId))
        }
        await Promise.all(promises)
    }

    async mounted() {
        await this.loadStates()
    }

    async getAllActiveLaboratories(): Promise<CommonLaboratoryResponse[]> {
        const response = await this.$_toCallUnique(commonLaboratoryService.getAllActiveLaboratories())
        return response ?? []
    }

    async doAction(): Promise<void> {
        const valid = await this.$validator.validateAll()
        if (valid) {
            if (this.state.id) {
                this.update()
            } else {
                this.create()
            }
        } else {
            this.$_showErrorModal(this.$tc('studyRequest.general.requiredFieldsNotice'))
        }
    }
}
